import { aitrasApi } from '@/state/api/aitrasApi';
import type {
  TenantFactorConfigEntryResponse,
  TenantFactorConfigEntryRequest,
  TenantLlmConnectorConfigResponse,
  TenantLlmConnectorConfigRequest,
  LlmConnectorSummary,
  CustomFactorResponse,
  CustomFactorRequest,
} from '@/types';

/**
 * Admin endpoints — all require the ADMIN project role (403 otherwise). Shapes
 * match ai-translation-risk-assessment-api exactly (see docs/API-contract.md).
 */
export const adminApi = aitrasApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── LLM connector config ──────────────────────────────────────────────
    getLlmConnectorConfig: builder.query<TenantLlmConnectorConfigResponse, void>({
      query: () => '/admin/llm-connector-config',
      providesTags: ['LlmConnectorConfig'],
    }),
    /**
     * Tenant LLM connectors from the gateway catalog (id + name). 503 when
     * the connector catalog is unavailable.
     */
    getLlmConnectors: builder.query<LlmConnectorSummary[], void>({
      query: () => '/admin/llm-connectors',
      providesTags: ['LlmConnectors'],
    }),
    putLlmConnectorConfig: builder.mutation<TenantLlmConnectorConfigResponse, TenantLlmConnectorConfigRequest>({
      query: (body) => ({ url: '/admin/llm-connector-config', method: 'PUT', body }),
      // The saved config is written from the PUT response rather than refetched:
      // while a refetch is pending the hook keeps returning the old data, so the
      // page would briefly show the stale saved id next to the save confirmation.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(adminApi.util.updateQueryData('getLlmConnectorConfig', undefined, () => data));
        } catch {
          // The failure reaches the caller through unwrap(); nothing to write.
        }
      },
    }),

    // ── Factor config ─────────────────────────────────────────────────────
    getFactorConfig: builder.query<TenantFactorConfigEntryResponse[], void>({
      query: () => '/admin/factor-config',
      providesTags: ['FactorConfig'],
    }),
    /** PUT accepts the FULL array of factor entries (backend replaces the config). */
    putFactorConfig: builder.mutation<TenantFactorConfigEntryResponse[], TenantFactorConfigEntryRequest[]>({
      query: (body) => ({ url: '/admin/factor-config', method: 'PUT', body }),
      invalidatesTags: ['FactorConfig'],
    }),

    // ── Custom factors ────────────────────────────────────────────────────
    getCustomFactors: builder.query<CustomFactorResponse[], void>({
      query: () => '/admin/custom-factors',
      providesTags: ['CustomFactors'],
    }),
    createCustomFactor: builder.mutation<CustomFactorResponse, CustomFactorRequest>({
      query: (body) => ({ url: '/admin/custom-factors', method: 'POST', body }),
      invalidatesTags: ['CustomFactors'],
    }),
    updateCustomFactor: builder.mutation<CustomFactorResponse, { id: string; body: CustomFactorRequest }>({
      query: ({ id, body }) => ({ url: `/admin/custom-factors/${id}`, method: 'PUT', body }),
      invalidatesTags: ['CustomFactors'],
    }),
    deleteCustomFactor: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/custom-factors/${id}`, method: 'DELETE' }),
      invalidatesTags: ['CustomFactors'],
    }),
  }),
});

export const {
  useGetLlmConnectorConfigQuery,
  useGetLlmConnectorsQuery,
  usePutLlmConnectorConfigMutation,
  useGetFactorConfigQuery,
  usePutFactorConfigMutation,
  useGetCustomFactorsQuery,
  useCreateCustomFactorMutation,
  useUpdateCustomFactorMutation,
  useDeleteCustomFactorMutation,
} = adminApi;
